import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, X } from "lucide-react";
import { EmptyState, LoadingState, ErrorState } from "@tabipla/ui";
import { useAdsInList, NativeAd, useAdContext } from "@tabipla/components";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { SpotCard } from "../components/spots/SpotCard";
import { spotCategories } from "../../config/categories";
import { useSpots } from "../../application/hooks/useSpots";
import { useAuth } from "../../application/hooks/useAuth";
import { cn } from "@tabipla/ui/lib/utils";
import { PlaceAutocomplete, type PlaceResult } from "../components/PlaceAutocomplete";
import type { MekkeSpotCategory, MekkeSpotStatus } from "@tabipla/shared";

const AD_SLOT_SPOTS = import.meta.env.VITE_AD_SLOT_SPOTS ?? "0000000000";

// Helper to get random item from array
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

type StatusFilter = "all" | MekkeSpotStatus;
type CategoryFilter = MekkeSpotCategory | "all";

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [showLocationFilter, setShowLocationFilter] = useState(false);

  // Memoize random greeting to keep it stable during the session
  const { greeting, welcomeMessage } = useMemo(() => {
    const hour = new Date().getHours();

    // Get time-based greeting variations
    let greetingVariations: string[];
    if (hour < 12) {
      greetingVariations = t("home.greetings.morningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.morning", "おはよう！")],
      }) as string[];
    } else if (hour < 18) {
      greetingVariations = t("home.greetings.afternoonVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.afternoon", "こんにちは！")],
      }) as string[];
    } else {
      greetingVariations = t("home.greetings.eveningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.evening", "こんばんは！")],
      }) as string[];
    }

    // Get welcome message variations
    const welcomeVariations = t("home.welcomeVariations", {
      returnObjects: true,
      defaultValue: ["{{name}}さんのスポット"],
    }) as string[];

    return {
      greeting: getRandomItem(greetingVariations),
      welcomeMessage: getRandomItem(welcomeVariations),
    };
  }, [t]);

  const handleLocationChange = useCallback((place: PlaceResult | null) => {
    if (place) {
      setLocationFilter(place.name || place.formattedAddress);
    } else {
      setLocationFilter("");
    }
  }, []);

  const adContext = useAdContext();

  const {
    data: spots,
    isLoading,
    error,
    refetch,
  } = useSpots({
    status: statusFilter,
    category: categoryFilter,
  });

  // Filter spots by search query and location
  const filteredSpots = useMemo(() => {
    return spots?.filter((spot) => {
      // Apply location filter first
      if (locationFilter.trim()) {
        const location = locationFilter.toLowerCase().trim();
        if (!spot.address?.toLowerCase().includes(location)) {
          return false;
        }
      }

      // Then apply text search
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase().trim();
      return (
        spot.name.toLowerCase().includes(query) ||
        spot.description?.toLowerCase().includes(query) ||
        spot.address?.toLowerCase().includes(query) ||
        spot.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [spots, locationFilter, searchQuery]);

  // 広告をリストに挿入（6件ごと、4件目以降）
  const spotsWithAds = useAdsInList(filteredSpots ?? [], {
    interval: 6,
    startAfter: 4,
    maxAds: 3,
    slotIds: [AD_SLOT_SPOTS],
    enabled: adContext?.config.enabled ?? false,
  });

  if (isLoading) {
    return (
      <MekkeLayout title={t("spots.title")}>
        <LoadingState message={t("common.loading")} />
      </MekkeLayout>
    );
  }

  if (error) {
    return (
      <MekkeLayout title={t("spots.title")}>
        <ErrorState error={error} onRetry={refetch} />
      </MekkeLayout>
    );
  }

  return (
    <MekkeLayout
      title={t("spots.title")}
      showSearch
      searchPlaceholder={t("spots.searchPlaceholder")}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      fabAction={{
        icon: Plus,
        label: t("spots.add"),
        onClick: () => navigate("/spots/new"),
      }}
    >
      <div className="animate-fade-in space-y-6">
        {/* Welcome Section */}
        <section>
          <p className="text-muted-foreground">{greeting}</p>
          <h1 className="text-2xl font-bold">
            {user?.name ? welcomeMessage.replace("{{name}}", user.name) : t("spots.title")}
          </h1>
        </section>

        {/* Filters Section */}
        <section className="space-y-3">
          {/* Status Filter */}
          <div className="flex flex-wrap gap-1.5">
            {(["all", "visited", "want_to_go"] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={cn(
                  "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  statusFilter === status
                    ? "bg-primary text-primary-foreground"
                    : "border border-border bg-background text-muted-foreground hover:bg-muted/50"
                )}
              >
                {status === "all"
                  ? t("spots.allStatuses")
                  : status === "visited"
                    ? t("spots.visited")
                    : t("spots.wantToGo")}
              </button>
            ))}
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setCategoryFilter("all")}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                categoryFilter === "all"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-muted/50"
              )}
            >
              {t("spots.allCategories")}
            </button>
            {spotCategories.map((category) => {
              const Icon = category.icon;
              const isSelected = categoryFilter === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setCategoryFilter(category.id)}
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    isSelected
                      ? "border-transparent text-white"
                      : "border-border bg-background text-muted-foreground hover:bg-muted/50"
                  )}
                  style={isSelected ? { backgroundColor: category.color } : undefined}
                >
                  <Icon className="h-3 w-3" />
                  {t(category.labelKey)}
                </button>
              );
            })}
          </div>

          {/* Location Filter Toggle & Input */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowLocationFilter(!showLocationFilter)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                showLocationFilter || locationFilter
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-muted/50"
              )}
            >
              <MapPin className="h-3 w-3" />
              {t("spots.locationFilter", "地域で絞り込み")}
            </button>
            {locationFilter && (
              <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {locationFilter}
                <button
                  onClick={() => setLocationFilter("")}
                  className="rounded-full p-0.5 hover:bg-background"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
          {showLocationFilter && (
            <PlaceAutocomplete
              value={locationFilter}
              onChange={handleLocationChange}
              placeholder={t("spots.locationFilterPlaceholder", "例: 京都、東京...")}
            />
          )}
        </section>

        {/* Spots Section */}
        <section className="space-y-4">
          {!filteredSpots || filteredSpots.length === 0 ? (
            <EmptyState
              icon={MapPin}
              title={searchQuery ? t("common.noResults") : t("spots.empty")}
              description={
                searchQuery
                  ? t("spots.noSearchResults", { query: searchQuery })
                  : t("spots.addFirst")
              }
              action={
                !searchQuery
                  ? {
                      label: t("spots.add"),
                      onClick: () => navigate("/spots/new"),
                      icon: Plus,
                    }
                  : undefined
              }
              variant="dashed"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {spotsWithAds.map((item) =>
                item.type === "ad" ? (
                  <div key={item.key} className="col-span-2">
                    <NativeAd slotId={item.slotId} variant="card" />
                  </div>
                ) : (
                  <SpotCard key={item.data.id} spot={item.data} />
                )
              )}
            </div>
          )}
        </section>
      </div>
    </MekkeLayout>
  );
}
